import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  SiPython, SiCplusplus, SiDotnet, SiPandas, SiNumpy, SiScikitlearn, SiTensorflow,
  SiPytorch, SiOpencv, SiHuggingface, SiGit, SiDocker,
} from 'react-icons/si';
import {
  FaRobot, FaBrain, FaImages, FaLayerGroup, FaCogs, FaChartLine, FaChartBar,
  FaFlask, FaUsers, FaDatabase, FaMicrosoft,
} from 'react-icons/fa';
import Corners from '../ui/Corners';
import Reveal from '../ui/Reveal';
import SectionHeader from '../ui/SectionHeader';
import './Skills.css';

const CATEGORIES = ['All', 'AI & ML', 'Data', 'Languages', 'Tools'];

const SKILLS = [
  { name: 'Machine Learning', icon: FaRobot, cat: 'AI & ML' },
  { name: 'Computer Vision', icon: SiOpencv, cat: 'AI & ML' },
  { name: 'Vision-Language Models', icon: FaImages, cat: 'AI & ML' },
  { name: 'NLP', icon: FaBrain, cat: 'AI & ML' },
  { name: 'Fine-tuning LLMs', icon: SiHuggingface, cat: 'AI & ML' },
  { name: 'Recommendation Systems', icon: FaLayerGroup, cat: 'AI & ML' },
  { name: 'PyTorch', icon: SiPytorch, cat: 'AI & ML' },
  { name: 'TensorFlow', icon: SiTensorflow, cat: 'AI & ML' },
  { name: 'Scikit-learn', icon: SiScikitlearn, cat: 'AI & ML' },
  { name: 'Pandas', icon: SiPandas, cat: 'Data' },
  { name: 'NumPy', icon: SiNumpy, cat: 'Data' },
  { name: 'Feature Engineering', icon: FaCogs, cat: 'Data' },
  { name: 'Statistics', icon: FaChartLine, cat: 'Data' },
  { name: 'Data Visualization', icon: FaChartBar, cat: 'Data' },
  { name: 'A/B Testing', icon: FaFlask, cat: 'Data' },
  { name: 'CRM Analytics', icon: FaUsers, cat: 'Data' },
  { name: 'SQL', icon: FaDatabase, cat: 'Data' },
  { name: 'Python', icon: SiPython, cat: 'Languages' },
  { name: 'C++', icon: SiCplusplus, cat: 'Languages' },
  { name: 'C#', icon: FaMicrosoft, cat: 'Languages' },
  { name: '.NET', icon: SiDotnet, cat: 'Tools' },
  { name: 'Git / GitHub', icon: SiGit, cat: 'Tools' },
  { name: 'Docker', icon: SiDocker, cat: 'Tools' },
];

const Skills = () => {
  const [cat, setCat] = useState('All');
  const visible = SKILLS.filter((s) => cat === 'All' || s.cat === cat);
  const countFor = (c) => (c === 'All' ? SKILLS.length : SKILLS.filter((s) => s.cat === c).length);

  return (
    <section id="skills" className="section skills">
      <div className="container">
        <SectionHeader eyebrow="skills" title="The toolkit." lede="Pick a category to filter." />

        <Reveal className="skills-filters" role="group" aria-label="Filter skills">
          {CATEGORIES.map((c) => {
            const isActive = cat === c;
            return (
              <button
                key={c}
                type="button"
                className={`skills-filter ${isActive ? 'is-active' : ''}`}
                onClick={() => setCat(c)}
                aria-pressed={isActive}
              >
                {isActive && (
                  <motion.span
                    layoutId="skills-filter-pill"
                    className="skills-filter-pill"
                    transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                  />
                )}
                <span className="skills-filter-label">{c}</span>
                <span className="skills-filter-count">{countFor(c)}</span>
              </button>
            );
          })}
        </Reveal>

        <motion.ul className="skills-grid" layout>
          <AnimatePresence mode="popLayout">
            {visible.map((s) => (
              <motion.li
                key={s.name}
                layout
                className="skill has-corners"
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ type: 'spring', stiffness: 420, damping: 34 }}
              >
                <span className="skill-icon" aria-hidden="true"><s.icon /></span>
                <span className="skill-name">{s.name}</span>
                <span className="skill-cat mono">{s.cat}</span>
                <Corners />
              </motion.li>
            ))}
          </AnimatePresence>
        </motion.ul>

        <p className="skills-count mono" aria-live="polite">
          showing {visible.length} / {SKILLS.length}
        </p>
      </div>
    </section>
  );
};

export default Skills;
